import React from 'react';

/**
 * Astra custom tool-icon set — a cohesive, modern, geometric glyph family drawn
 * on a 24×24 grid with a consistent 1.7px round stroke. Every icon paints with
 * `currentColor`, so the per-function colour applied by ToolButton flows straight
 * through and the toolbar reads as a clean multicolour set. Size comes from the
 * `className` (e.g. h-7 w-7) passed by Icons.ByName.
 */

type IconSvgProps = React.SVGProps<SVGSVGElement> & { className?: string };

// Shared frame: fixed viewBox, transparent fill, and a stroked group so each
// icon only needs to declare its paths.
const Glyph = ({ children, ...props }: IconSvgProps & { children: React.ReactNode }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <g
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    >
      {children}
    </g>
  </svg>
);

const Dot = ({ cx, cy, r = 1.5 }: { cx: number; cy: number; r?: number }) => (
  <circle
    cx={cx}
    cy={cy}
    r={r}
    fill="currentColor"
    stroke="none"
  />
);

/** Pan — four-way move arrows. */
export const AstraPan = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="M12 4.2v15.6M4.2 12h15.6" />
    <path d="M12 4.2 9.9 6.5M12 4.2l2.1 2.3" />
    <path d="M12 19.8l-2.1-2.3M12 19.8l2.1-2.3" />
    <path d="M4.2 12 6.5 9.9M4.2 12l2.3 2.1" />
    <path d="M19.8 12l-2.3-2.1M19.8 12l-2.3 2.1" />
  </Glyph>
);

/** Zoom — magnifier with a plus. */
export const AstraZoom = (props: IconSvgProps) => (
  <Glyph {...props}>
    <circle cx="10.5" cy="10.5" r="6.3" />
    <path d="M19.5 19.5 15 15" />
    <path d="M10.5 8v5M8 10.5h5" />
  </Glyph>
);

/** Window / Level — split contrast disc. */
export const AstraWindowLevel = (props: IconSvgProps) => (
  <Glyph {...props}>
    <circle cx="12" cy="12" r="8" />
    <path
      d="M12 4a8 8 0 0 1 0 16Z"
      fill="currentColor"
      stroke="none"
    />
  </Glyph>
);

/** Stack scroll — slice frame with up/down navigation. */
export const AstraStackScroll = (props: IconSvgProps) => (
  <Glyph {...props}>
    <rect
      x="6.5"
      y="4"
      width="11"
      height="16"
      rx="1.8"
    />
    <path d="M12 8.2v7.6" />
    <path d="M9.9 10.3 12 8.2l2.1 2.1" />
    <path d="M9.9 13.7 12 15.8l2.1-2.1" />
  </Glyph>
);

/** Cine — play disc. */
export const AstraCine = (props: IconSvgProps) => (
  <Glyph {...props}>
    <circle cx="12" cy="12" r="8" />
    <path
      d="M10.2 8.4 15.2 12l-5 3.6Z"
      fill="currentColor"
      stroke="none"
    />
  </Glyph>
);

/** Length — measurement line with end nodes. */
export const AstraLength = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="M5.5 18.5 18.5 5.5" />
    <Dot
      cx={5.5}
      cy={18.5}
      r={1.7}
    />
    <Dot
      cx={18.5}
      cy={5.5}
      r={1.7}
    />
  </Glyph>
);

/** Capture — camera. */
export const AstraCapture = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2L9 4.8h6L16.5 7h2A1.5 1.5 0 0 1 20 8.5v9A1.5 1.5 0 0 1 18.5 19h-13A1.5 1.5 0 0 1 4 17.5Z" />
    <circle cx="12" cy="13" r="3.4" />
  </Glyph>
);

/** Crosshairs. */
export const AstraCrosshair = (props: IconSvgProps) => (
  <Glyph {...props}>
    <circle cx="12" cy="12" r="7.5" />
    <path d="M12 2.6v4.2M12 17.2v4.2M2.6 12h4.2M17.2 12h4.2" />
    <Dot
      cx={12}
      cy={12}
      r={1.4}
    />
  </Glyph>
);

/** Invert — split disc (mirror of window/level). */
export const AstraInvert = (props: IconSvgProps) => (
  <Glyph {...props}>
    <circle cx="12" cy="12" r="8" />
    <path
      d="M12 4a8 8 0 0 0 0 16Z"
      fill="currentColor"
      stroke="none"
    />
  </Glyph>
);

/** Reset — circular refresh arrow. */
export const AstraReset = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="M19 12a7 7 0 1 1-2.05-4.95" />
    <path d="M19 4.5V8.2h-3.7" />
  </Glyph>
);

/** Rotate right — clockwise arc arrow. */
export const AstraRotateRight = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="M5 12a7 7 0 1 0 2.05-4.95" />
    <path d="M5 4.5V8.2h3.7" />
  </Glyph>
);

/** Reference lines — frame with crossing guide lines. */
export const AstraReferenceLines = (props: IconSvgProps) => (
  <Glyph {...props}>
    <rect
      x="4"
      y="4"
      width="16"
      height="16"
      rx="2"
    />
    <path
      d="M4 9.5h16M9.5 4v16"
      strokeDasharray="1.6 2.4"
    />
  </Glyph>
);

/** Image overlay — overlapping layers. */
export const AstraOverlay = (props: IconSvgProps) => (
  <Glyph {...props}>
    <rect
      x="3.8"
      y="7.6"
      width="10.6"
      height="10.6"
      rx="1.8"
    />
    <path d="M9.6 7.6V5.6A1.6 1.6 0 0 1 11.2 4h7.2A1.6 1.6 0 0 1 20 5.6v7.2a1.6 1.6 0 0 1-1.6 1.6h-2" />
  </Glyph>
);

/** Magnify (zoom-in loupe). */
export const AstraMagnify = (props: IconSvgProps) => (
  <Glyph {...props}>
    <circle cx="10.5" cy="10.5" r="6.3" />
    <path d="M19.5 19.5 15 15" />
    <circle cx="10.5" cy="10.5" r="2.4" />
  </Glyph>
);

/** Bidirectional — long + short perpendicular axes. */
export const AstraBidirectional = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="M4.5 15.5 15.5 4.5" />
    <path d="M9 8.6 15.4 15" />
    <Dot
      cx={4.5}
      cy={15.5}
      r={1.5}
    />
    <Dot
      cx={15.5}
      cy={4.5}
      r={1.5}
    />
  </Glyph>
);

/** Arrow annotate. */
export const AstraAnnotate = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="M19 5 7.5 16.5" />
    <path d="M7 11.5V17h5.5" />
  </Glyph>
);

/** Elliptical ROI. */
export const AstraEllipse = (props: IconSvgProps) => (
  <Glyph {...props}>
    <ellipse
      cx="12"
      cy="12"
      rx="9"
      ry="6.4"
    />
  </Glyph>
);

/** Rectangle ROI. */
export const AstraRectangle = (props: IconSvgProps) => (
  <Glyph {...props}>
    <rect
      x="3.5"
      y="6"
      width="17"
      height="12"
      rx="1.6"
    />
  </Glyph>
);

/** Circle ROI. */
export const AstraCircle = (props: IconSvgProps) => (
  <Glyph {...props}>
    <circle cx="12" cy="12" r="8" />
  </Glyph>
);

/** Angle — two rays from a vertex with an arc. */
export const AstraAngle = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="M5 18.5 20 18.5" />
    <path d="M5 18.5 17.5 6" />
    <path d="M11 18.5A6 6 0 0 0 8.6 13.7" />
  </Glyph>
);

/** Probe — HU target. */
export const AstraProbe = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="M12 3.5v4M12 16.5v4M3.5 12h4M16.5 12h4" />
    <circle cx="12" cy="12" r="3.2" />
    <Dot
      cx={12}
      cy={12}
      r={0.9}
    />
  </Glyph>
);

/** Flip horizontal — mirrored wedges across a dashed axis. */
export const AstraFlipHorizontal = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path
      d="M12 3.6v16.8"
      strokeDasharray="2.6 2.6"
    />
    <path d="M8.8 7.6 4 12l4.8 4.4Z" />
    <path
      d="M15.2 7.6 20 12l-4.8 4.4Z"
      fill="currentColor"
      stroke="none"
      opacity="0.55"
    />
  </Glyph>
);

/** Cobb angle — two offset segments + connector. */
export const AstraCobb = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="M4 7.4 15 4.6M9 19.4l11-2.8" />
    <path
      d="m13.4 9 -2.2 5.8"
      strokeDasharray="2.4 2.2"
    />
    <Dot cx={4} cy={7.4} r={1.3} />
    <Dot cx={20} cy={16.6} r={1.3} />
  </Glyph>
);

/** Freehand ROI — open loop. */
export const AstraFreehand = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="M7.2 17.6c-3.1-2.2-3.4-6.2-.7-8.7 2.3-2.1 5.8-2.3 8.4-.8 2.9 1.5 4.3 4.5 2.9 7-1.3 2.2-4.2 3.1-6.5 2.1-1.6-.8-2.2-2.5-1.4-3.8.6-1.1 2.1-1.5 3.2-.9" />
  </Glyph>
);

/** Spline ROI — smooth curve with control points. */
export const AstraSpline = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="M4 18c4.6 1.4 5.4-7.4 9.4-9.4 2.4-1.2 5 .3 6.6 2.6" />
    <Dot cx={4} cy={18} r={1.4} />
    <Dot cx={13.4} cy={8.6} r={1.4} />
    <Dot cx={20} cy={11.2} r={1.4} />
  </Glyph>
);

/** Livewire — magnetic zig-zag path. */
export const AstraLivewire = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="m4 17 4.6-2.4 2.8 1.6 3.2-4.4 2.6 1 2.8-5.6" />
    <Dot cx={4} cy={17} r={1.3} />
    <Dot cx={20} cy={7.2} r={1.3} />
  </Glyph>
);

/** Calibration — angled ruler with tick marks. */
export const AstraCalibration = (props: IconSvgProps) => (
  <Glyph {...props}>
    <rect
      x="2.9"
      y="9.1"
      width="18.2"
      height="5.8"
      rx="1.5"
      transform="rotate(-18 12 12)"
    />
    <path d="m7.3 13.3.6 1.8M10.7 12.1l.4 1.3M13.9 11l.6 1.8M17.3 9.8l.4 1.3" />
  </Glyph>
);

/** Ultrasound directional — probe fan with measure cross. */
export const AstraUltrasound = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="M9.2 4h5.6l3.3 13.2a1.8 1.8 0 0 1-1.8 2.8H7.7a1.8 1.8 0 0 1-1.8-2.8Z" />
    <path d="m9 9.6 6 5M15 9.6l-6 5" />
  </Glyph>
);

/** Magnify probe (loupe) — lens with inner arc. */
export const AstraLoupe = (props: IconSvgProps) => (
  <Glyph {...props}>
    <circle cx="10.5" cy="10.5" r="6.3" />
    <path d="M19.5 19.5 15 15" />
    <path d="M7.7 10.5a2.8 2.8 0 0 1 2.8-2.8" />
  </Glyph>
);

/** Window-level region — dashed selection + contrast disc. */
export const AstraWindowRegion = (props: IconSvgProps) => (
  <Glyph {...props}>
    <rect
      x="3.6"
      y="5"
      width="16.8"
      height="14"
      rx="2.2"
      strokeDasharray="3.2 2.6"
    />
    <circle cx="12" cy="12" r="3.4" />
    <path
      d="M12 8.6a3.4 3.4 0 0 1 0 6.8Z"
      fill="currentColor"
      stroke="none"
      opacity="0.55"
    />
  </Glyph>
);

/** 3D rotate — sphere with orbit + arrow. */
export const Astra3DRotate = (props: IconSvgProps) => (
  <Glyph {...props}>
    <circle cx="12" cy="12" r="7.2" />
    <ellipse
      cx="12"
      cy="12"
      rx="7.2"
      ry="2.8"
      opacity="0.55"
    />
    <path d="M21.4 9.4a10 10 0 0 0-1.5-2.1m.5 4.4.9-2.2 2.2.8" />
  </Glyph>
);

/** Link / sync — two chain links. */
export const AstraLink = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="M10 14.2 14 10" />
    <path d="m12.8 7.2 1.8-1.8a3.6 3.6 0 0 1 5.1 5.1l-1.8 1.8" />
    <path
      d="m11.2 16.8-1.8 1.8a3.6 3.6 0 0 1-5.1-5.1l1.8-1.8"
      opacity="0.7"
    />
  </Glyph>
);

/** DICOM tag browser — code angle brackets. */
export const AstraTagBrowser = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="M8.4 5.4 4 12l4.4 6.6M15.6 5.4 20 12l-4.4 6.6" />
    <path
      d="m13 4.8-2 14.4"
      opacity="0.55"
    />
  </Glyph>
);

/** Segment label — tag with pin hole. */
export const AstraSegmentLabel = (props: IconSvgProps) => (
  <Glyph {...props}>
    <path d="M4 10.6V5.6A1.6 1.6 0 0 1 5.6 4h5a2 2 0 0 1 1.4.6l7.4 7.4a2 2 0 0 1 0 2.8l-5 5a2 2 0 0 1-2.8 0L4.6 12a2 2 0 0 1-.6-1.4Z" />
    <Dot cx={8.3} cy={8.3} r={1.3} />
  </Glyph>
);
